import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Clock,
  Filter,
  Mail,
  FileWarning,
  LayoutGrid,
  Pause,
  Play,
  Zap,
  Settings,
  MessageSquare,
  X,
  Send,
  Timer,
  BellOff,
} from "lucide-react";
import { SearchBar, Button } from "./ui";
import ModelBuildingManagerCard from "./ModelBuildingManagerCard";
import ModelBuildingManagerModal from "./ModelBuildingManagerModal";
import OnHoldReminderModal from "./OnHoldReminderModal";
import HoldProjectModal from "./HoldProjectModal";
import RushProjectReminderModal from "./RushProjectReminderModal";
import DiscrepancyModal from "./Discrepancies/DiscrepancyModal";
import DiscrepancyCard from "./DiscrepancyCard";
import AssignmentRulesManager from "./AssignmentRulesManager";
import LoadingSpinner from "./LoadingSpinner";
import ErrorMessage from "./ErrorMessage";
import { ProjectService } from "../services/projectService";
import { DiscrepancyService } from "../services/discrepancyService";
import { KPIService } from "../services/kpiService";
import { parseCurrentUrl, generateModelBuildingManagerUrl } from "../utils/urlManager";
import { calculateBusinessDays } from "../utils/dateFormatting";
import "./ModelBuildingManagerDashboard.css";
import "./KPIBoard/KPIBoard.css"; // For slack modal styles

const ModelBuildingManagerDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Parse URL to get active tab
  const { tab: urlTab } = parseCurrentUrl(location.pathname);
  const initialTab = urlTab || "action-items";

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterHoldReason, setFilterHoldReason] = useState("all");
  const [sortBy, setSortBy] = useState("holdStartDate");
  const [sortDirection] = useState("desc");
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showAllHoldProjects, setShowAllHoldProjects] = useState(false); // Toggle between pending and all hold projects
  const [emailTrackingData, setEmailTrackingData] = useState([]);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState(null);
  const [discrepancyModalOpen, setDiscrepancyModalOpen] = useState(false);
  const [selectedHoldProject, setSelectedHoldProject] = useState(null);
  const [holdReminderModalOpen, setHoldReminderModalOpen] = useState(false);
  const [recentHoldReminders, setRecentHoldReminders] = useState([]);
  const [showRecentReminders, setShowRecentReminders] = useState(false);
  const [rushProjectTracking, setRushProjectTracking] = useState([]);
  const [selectedRushProject, setSelectedRushProject] = useState(null);
  const [rushReminderModalOpen, setRushReminderModalOpen] = useState(false);
  const [recentRushNotifications, setRecentRushNotifications] = useState([]);
  const [showRecentRushNotifications, setShowRecentRushNotifications] = useState(false);
  const [holdModalProject, setHoldModalProject] = useState(null);
  const [showHoldModal, setShowHoldModal] = useState(false);

  // Stale in stage tracking
  const [staleSnoozeData, setStaleSnoozeData] = useState(() => {
    try {
      const stored = localStorage.getItem('staleInStageSnooze');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const [selectedStaleProject, setSelectedStaleProject] = useState(null);
  const [showStaleSlackModal, setShowStaleSlackModal] = useState(false);
  const [staleSlackMessage, setStaleSlackMessage] = useState('');
  const [staleSending, setStaleSending] = useState(false);

  // Sync active tab with URL
  useEffect(() => {
    const { tab: urlTab } = parseCurrentUrl(location.pathname);
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [location.pathname]);

  // Load data on mount
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        allProjects,
        clientsData,
        contactsData,
        trackingData,
        discrepanciesData,
        recentRemindersData,
        rushTrackingData,
        recentRushData
      ] = await Promise.all([
        ProjectService.getAllProjects(),
        ProjectService.getClients(),
        ProjectService.getContacts(),
        ProjectService.getEmailTracking(),
        ProjectService.getDiscrepancies(),
        ProjectService.getRecentHoldReminders(),
        ProjectService.getRushProjectTracking(),
        ProjectService.getRecentRushNotifications(),
      ]);

      setProjects(allProjects);
      setClients(clientsData);
      setContacts(contactsData);
      setEmailTrackingData(trackingData || []);
      setDiscrepancies(discrepanciesData || []);
      setRecentHoldReminders(recentRemindersData || []);
      setRushProjectTracking(rushTrackingData || []);
      setRecentRushNotifications(recentRushData || []);
    } catch (err) {
      console.error("Error loading data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter for projects on hold
  const holdProjects = useMemo(() => {
    // Only show projects that are on Hold AND have a hold start date AND not completed
    const onHoldProjects = projects.filter((project) => {
      const modelingStatus = project.modelingStatus || project.modeling_status;
      const isNotCompleted = modelingStatus !== 'Completed' && modelingStatus !== 'completed';
      return project.modelingState === "Hold" && project.holdStartDate && isNotCompleted;
    });

    // When showAllHoldProjects is false, only show projects where it's time to send an email
    if (!showAllHoldProjects) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison

      return onHoldProjects.filter((project) => {
        // Get tracking data for this project
        const tracking = emailTrackingData.find(t => t.project_id === project.id);

        // Determine if this is initial or recurring email
        const hasInitialEmailBeenSent = tracking?.last_email_sent_date;

        if (!hasInitialEmailBeenSent) {
          // INITIAL EMAIL: Show when holdStartDate + initial_follow_up_days <= today
          const initialDays = tracking?.initial_follow_up_days || project.holdInitialFollowUpDays || 14;
          const holdStart = new Date(project.holdStartDate);
          holdStart.setHours(0, 0, 0, 0);

          const dueDate = new Date(holdStart);
          dueDate.setDate(dueDate.getDate() + initialDays);

          return dueDate <= today;
        } else {
          // RECURRING EMAIL: Show when last_email_sent_date + recurring_follow_up_days <= today
          const recurringDays = tracking?.recurring_follow_up_days || project.holdRecurringFollowUpDays || 7;
          const lastSent = new Date(tracking.last_email_sent_date);
          lastSent.setHours(0, 0, 0, 0);

          const dueDate = new Date(lastSent);
          dueDate.setDate(dueDate.getDate() + recurringDays);

          return dueDate <= today;
        }
      });
    }

    return onHoldProjects;
  }, [projects, showAllHoldProjects, emailTrackingData]);


  // Filter for projects on hold that need email setup (for Action Items view)
  const holdProjectsPendingEmail = useMemo(() => {
    // Only show projects that are on Hold AND have a hold start date AND not completed
    const onHoldProjects = projects.filter((project) => {
      const modelingStatus = project.modelingStatus || project.modeling_status;
      const isNotCompleted = modelingStatus !== 'Completed' && modelingStatus !== 'completed';
      return project.modelingState === "Hold" && project.holdStartDate && isNotCompleted;
    });

    // Filter to only projects where it's time to send an email (based on day intervals)
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison

    return onHoldProjects.filter((project) => {
      // Get tracking data for this project
      const tracking = emailTrackingData.find(t => t.project_id === project.id);

      // Determine if this is initial or recurring email
      const hasInitialEmailBeenSent = tracking?.last_email_sent_date;

      // Calculate if project is due for email based on dates
      let isDue = false;

      if (!hasInitialEmailBeenSent) {
        // INITIAL EMAIL: Check if holdStartDate + initial_follow_up_days <= today
        const initialDays = tracking?.initial_follow_up_days || project.holdInitialFollowUpDays || 14;
        const holdStart = new Date(project.holdStartDate);
        holdStart.setHours(0, 0, 0, 0);

        const dueDate = new Date(holdStart);
        dueDate.setDate(dueDate.getDate() + initialDays);

        isDue = dueDate <= today;
      } else {
        // RECURRING EMAIL: Check if last_email_sent_date + recurring_follow_up_days <= today
        const recurringDays = tracking?.recurring_follow_up_days || project.holdRecurringFollowUpDays || 7;
        const lastSent = new Date(tracking.last_email_sent_date);
        lastSent.setHours(0, 0, 0, 0);

        const dueDate = new Date(lastSent);
        dueDate.setDate(dueDate.getDate() + recurringDays);

        isDue = dueDate <= today;
      }

      return isDue;
    });
  }, [projects, emailTrackingData]);

  // Filter for discrepancies that need review - return flat list with project info
  const discrepanciesForReview = useMemo(() => {
    // Filter discrepancies that are specifically "Waiting to Review"
    const reviewDiscrepancies = discrepancies.filter((disc) => {
      // The database column is 'status', not 'discrepancy_status'
      const status = disc.status || disc.discrepancyStatus;

      // Only include discrepancies that are "Waiting to Review"
      return status === 'Waiting to Review';
    });

    // Map each discrepancy to include its project info
    return reviewDiscrepancies.map(discrepancy => {
      const project = projects.find(p => p.id === discrepancy.project_id);

      // Skip if project is completed or not found
      if (!project) return null;
      const modelingStatus = project.modelingStatus || project.modeling_status;
      if (modelingStatus === 'Completed' || modelingStatus === 'completed') return null;

      return {
        discrepancy,
        project
      };
    }).filter(Boolean); // Remove null entries
  }, [projects, discrepancies]);

  // Filter for rush projects that need attention
  const rushProjectsPendingNotification = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Filter projects that:
    // 1. Have tracking record with status 'pending'
    // 2. Not snoozed or snooze period has expired
    // 3. Not completed
    return projects.filter((project) => {
      const modelingStatus = project.modelingStatus || project.modeling_status;
      const isNotCompleted = modelingStatus !== 'Completed' && modelingStatus !== 'completed';

      // Find tracking record
      const tracking = rushProjectTracking.find(t => t.project_id === project.id);
      if (!tracking) return false;

      // Check if pending
      if (tracking.email_status !== 'pending') return false;

      // Check if snoozed and if snooze period has expired
      if (tracking.snoozed_until) {
        const snoozeDate = new Date(tracking.snoozed_until);
        snoozeDate.setHours(0, 0, 0, 0);
        if (snoozeDate > today) return false; // Still snoozed
      }

      return isNotCompleted;
    }).map(project => {
      const tracking = rushProjectTracking.find(t => t.project_id === project.id);
      return {
        ...project,
        rushTracking: tracking
      };
    });
  }, [projects, rushProjectTracking]);

  // Filter for projects on soft hold for 3+ days (not on full Hold)
  const softHoldPausedProjects = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const threeDaysAgo = new Date(today);
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

    return projects.filter((project) => {
      // Must be on soft hold with a date
      if (!project.isSoftHold || !project.softHoldDate) return false;
      // Exclude projects already on full Hold
      if (project.modelingState === 'Hold') return false;
      // Exclude completed projects
      const modelingStatus = project.modelingStatus || project.modeling_status;
      if (modelingStatus === 'Completed' || modelingStatus === 'completed') return false;

      const softHoldDate = new Date(project.softHoldDate);
      softHoldDate.setHours(0, 0, 0, 0);
      return softHoldDate <= threeDaysAgo;
    });
  }, [projects]);

  // Calculate days paused
  const calculateDaysPaused = (softHoldDate) => {
    if (!softHoldDate) return 0;
    const start = new Date(softHoldDate);
    const now = new Date();
    return Math.floor((now - start) / (1000 * 60 * 60 * 24));
  };

  // Filter for projects stale in stage (3+ business days in same modeling_status)
  const staleInStageProjects = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return projects.filter((project) => {
      // Must have a last_stage_change_date
      const lastStageChangeDate = project.lastStageChangeDate || project.last_stage_change_date;
      if (!lastStageChangeDate) return false;

      // Exclude completed or cancelled projects
      const modelingStatus = project.modelingStatus || project.modeling_status;
      if (!modelingStatus || modelingStatus === 'Completed' || modelingStatus === 'Cancelled') return false;

      // Exclude projects on Hold
      if (project.modelingState === 'Hold') return false;

      // Exclude projects on soft hold (they show in the Paused section)
      if (project.isSoftHold) return false;

      // Check if snoozed (key includes stage so snooze resets when stage changes)
      const snoozeKey = `${project.id}-${modelingStatus}`;
      const snoozeExpiry = staleSnoozeData[snoozeKey];
      if (snoozeExpiry) {
        const expiryDate = new Date(snoozeExpiry);
        if (expiryDate > today) return false; // Still snoozed
      }

      // Check if 3+ business days in current stage
      const businessDaysInStage = calculateBusinessDays(lastStageChangeDate, today);
      return businessDaysInStage >= 3;
    }).map((project) => {
      // Add calculated business days in stage
      const lastStageChangeDate = project.lastStageChangeDate || project.last_stage_change_date;
      const businessDaysInStage = calculateBusinessDays(lastStageChangeDate, today);
      return {
        ...project,
        daysInStage: businessDaysInStage,
      };
    }).sort((a, b) => b.daysInStage - a.daysInStage); // Sort by days in stage descending
  }, [projects, staleSnoozeData]);

  // Handle click on paused project card - open Hold modal
  const handlePausedProjectClick = (project) => {
    setHoldModalProject(project);
    setShowHoldModal(true);
  };

  // Handle "Make Active" button click
  const handleMakeActive = async (project) => {
    try {
      await ProjectService.updateProject(project.id, { is_soft_hold: false });
      // Refresh data
      loadInitialData();
    } catch (err) {
      console.error('Error making project active:', err);
    }
  };

  // Handle stale in stage - open Slack modal
  const handleStaleProjectClick = (project) => {
    const modelingStatus = project.modelingStatus || project.modeling_status;
    setSelectedStaleProject(project);
    setStaleSlackMessage(`This job has been in ${modelingStatus} for ${project.daysInStage} business days.`);
    setShowStaleSlackModal(true);
  };

  // Handle snooze stale project (resets 3-day clock)
  const handleSnoozeStaleProject = (project) => {
    const modelingStatus = project.modelingStatus || project.modeling_status;
    const snoozeKey = `${project.id}-${modelingStatus}`;
    const snoozeDays = 3;
    const snoozeUntil = new Date();
    snoozeUntil.setDate(snoozeUntil.getDate() + snoozeDays);

    const newSnoozeData = {
      ...staleSnoozeData,
      [snoozeKey]: snoozeUntil.toISOString(),
    };
    setStaleSnoozeData(newSnoozeData);
    localStorage.setItem('staleInStageSnooze', JSON.stringify(newSnoozeData));
  };

  // Handle dismiss stale project (permanently dismisses until stage changes)
  const handleDismissStaleProject = (project) => {
    const modelingStatus = project.modelingStatus || project.modeling_status;
    const snoozeKey = `${project.id}-${modelingStatus}`;
    // Set a far future date to effectively dismiss permanently
    const dismissUntil = new Date();
    dismissUntil.setFullYear(dismissUntil.getFullYear() + 1);

    const newSnoozeData = {
      ...staleSnoozeData,
      [snoozeKey]: dismissUntil.toISOString(),
    };
    setStaleSnoozeData(newSnoozeData);
    localStorage.setItem('staleInStageSnooze', JSON.stringify(newSnoozeData));
  };

  // Handle send Slack message for stale project
  const handleSendStaleSlackMessage = async () => {
    if (!selectedStaleProject || !staleSlackMessage.trim()) return;

    const channelId = selectedStaleProject.slackChannelId || selectedStaleProject.slack_channel_id;
    if (!channelId) {
      console.error('No Slack channel for project');
      return;
    }

    try {
      setStaleSending(true);
      await KPIService.queueSlackMessage({
        channelId,
        message: staleSlackMessage.trim(),
        projectId: selectedStaleProject.id,
        actionItemId: null,
        triggeredByEmail: null,
      });

      // Snooze after sending message
      handleSnoozeStaleProject(selectedStaleProject);

      setShowStaleSlackModal(false);
      setSelectedStaleProject(null);
      setStaleSlackMessage('');
    } catch (err) {
      console.error('Error sending Slack message:', err);
    } finally {
      setStaleSending(false);
    }
  };

  // Group projects by hold reason - commented out as not currently used
  // const projectsByHoldReason = useMemo(() => {
  //   const grouped = {
  //     waiting_discrepancy_clarification: [],
  //     client_requested_hold: [],
  //     waiting_for_information: [],
  //     internal_review: [],
  //     other: [],
  //   };

  //   holdProjects.forEach((project) => {
  //     const reason = project.holdReason || "other";
  //     if (grouped[reason]) {
  //       grouped[reason].push(project);
  //     } else {
  //       grouped.other.push(project);
  //     }
  //   });

  //   return grouped;
  // }, [holdProjects]);

  // Calculate statistics for tabs
  const statistics = useMemo(() => {
    return {
      totalOnHold: holdProjects.length,
      totalActionItems: discrepanciesForReview.length + holdProjectsPendingEmail.length + rushProjectsPendingNotification.length + softHoldPausedProjects.length + staleInStageProjects.length,
    };
  }, [holdProjects, discrepanciesForReview, holdProjectsPendingEmail, rushProjectsPendingNotification, softHoldPausedProjects, staleInStageProjects]);

  // Apply filters and search for Projects on Hold tab
  const filteredHoldProjects = useMemo(() => {
    let filtered = [...holdProjects];

    // Apply search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (project) =>
          project.projectName?.toLowerCase().includes(search) ||
          project.jobNumber?.toLowerCase().includes(search) ||
          project.clientName?.toLowerCase().includes(search) ||
          project.holdReason?.toLowerCase().includes(search) ||
          project.holdNotes?.toLowerCase().includes(search)
      );
    }

    // Apply hold reason filter or auto-recurring email filter
    if (filterHoldReason !== "all") {
      if (filterHoldReason === "auto_recurring_email") {
        // Filter for projects with auto-recurring email enabled
        filtered = filtered.filter((project) => {
          const hasAutoRecurring = emailTrackingData.some(
            tracking => tracking.project_id === project.id &&
            tracking.auto_recurring_email === true
          );
          return hasAutoRecurring;
        });
      } else {
        // Filter by hold reason
        filtered = filtered.filter(
          (project) => project.holdReason === filterHoldReason
        );
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case "holdStartDate":
          compareValue = new Date(b.holdStartDate || 0) - new Date(a.holdStartDate || 0);
          break;
        case "projectName":
          compareValue = (a.projectName || "").localeCompare(b.projectName || "");
          break;
        case "jobNumber":
          compareValue = (a.jobNumber || "").localeCompare(b.jobNumber || "");
          break;
        case "daysOnHold":
          const now = new Date();
          const aDays = a.holdStartDate ? Math.floor((now - new Date(a.holdStartDate)) / (1000 * 60 * 60 * 24)) : 0;
          const bDays = b.holdStartDate ? Math.floor((now - new Date(b.holdStartDate)) / (1000 * 60 * 60 * 24)) : 0;
          compareValue = bDays - aDays;
          break;
        default:
          compareValue = 0;
      }

      return sortDirection === "asc" ? compareValue : -compareValue;
    });

    return filtered;
  }, [holdProjects, searchQuery, filterHoldReason, sortBy, sortDirection, emailTrackingData]);

  // Handle card click
  const handleCardClick = (project) => {
    console.log("ModelBuildingManager: Selected project FULL DATA:", project);
    // For other tabs, open the regular project modal
    console.log("ModelBuildingManager: Hold fields specifically:", {
      id: project.id,
      name: project.projectName,
      holdStartDate: project.holdStartDate,
      holdReason: project.holdReason,
      holdInitialFollowUpDays: project.holdInitialFollowUpDays,
      holdNotes: project.holdNotes,
      modelingState: project.modelingState
    });
    setSelectedProject(project);
    setModalOpen(true);
  };

  // Handle discrepancy card click
  const handleDiscrepancyClick = (discrepancy) => {
    console.log("ModelBuildingManager: Selected discrepancy:", discrepancy);
    setSelectedDiscrepancy(discrepancy);
    setDiscrepancyModalOpen(true);
  };

  // Handle hold reminder card click
  const handleHoldReminderClick = (project) => {
    console.log("ModelBuildingManager: Opening hold reminder for project:", project);
    console.log("ModelBuildingManager: Current modal states BEFORE:", { modalOpen, holdReminderModalOpen, selectedProject: selectedProject?.id });

    // Close regular modal if it's open
    setModalOpen(false);
    setSelectedProject(null);

    // Open hold reminder modal
    setSelectedHoldProject(project);
    setHoldReminderModalOpen(true);
    console.log("ModelBuildingManager: Set holdReminderModalOpen=true, cleared modalOpen");
  };

  // Handle undo hold reminder
  const handleUndoHoldReminder = async (reminder) => {
    console.log("🔄 Undo hold reminder clicked", reminder);
    try {
      console.log("🔄 Calling ProjectService.undoHoldReminder", { projectId: reminder.project_id, trackingId: reminder.id });
      const result = await ProjectService.undoHoldReminder(reminder.project_id, reminder.id);
      console.log("✅ Undo successful", result);
      // Reload data to reflect changes
      console.log("🔄 Reloading data...");
      await loadInitialData();
      console.log("✅ Data reloaded");
    } catch (error) {
      console.error("❌ Error undoing hold reminder:", error);
    }
  };

  // Handle rush project reminder card click
  const handleRushProjectReminderClick = (project) => {
    console.log("ModelBuildingManager: Opening rush project reminder for project:", project);

    // Close regular modal if it's open
    setModalOpen(false);
    setSelectedProject(null);
    setHoldReminderModalOpen(false);
    setSelectedHoldProject(null);

    // Open rush reminder modal
    setSelectedRushProject(project);
    setRushReminderModalOpen(true);
  };

  // Handle undo rush notification
  const handleUndoRushNotification = async (notification) => {
    console.log("🔄 Undo rush notification clicked", notification);
    try {
      console.log("🔄 Calling ProjectService.undoRushNotification", { projectId: notification.project_id, trackingId: notification.id });
      const result = await ProjectService.undoRushNotification(notification.project_id, notification.id);
      console.log("✅ Undo successful", result);
      // Reload data to reflect changes
      console.log("🔄 Reloading data...");
      await loadInitialData();
      console.log("✅ Data reloaded");
    } catch (error) {
      console.error("❌ Error undoing rush notification:", error);
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedProject(null);
  };

  // Handle modal save
  const handleModalSave = async () => {
    await loadInitialData();
  };

  // Handle project delete
  const handleProjectDelete = async () => {
    await loadInitialData();
  };


  // Format hold reason for display - commented out as not currently used
  // const formatHoldReason = (reason) => {
  //   const reasonMap = {
  //     waiting_discrepancy_clarification: "Waiting for Discrepancy Clarification",
  //     client_requested_hold: "Client Requested Hold",
  //     waiting_for_information: "Waiting for Information",
  //     internal_review: "Internal Review",
  //     other: "Other",
  //   };
  //   return reasonMap[reason] || reason || "Unknown";
  // };

  // Calculate days on hold
  const calculateDaysOnHold = (holdStartDate) => {
    if (!holdStartDate) return 0;
    const start = new Date(holdStartDate);
    const now = new Date();
    // Set to start of day for accurate calculation
    start.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    
    const diffTime = now - start;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // Add 1 to include today (day 1 is the day it was placed on hold)
    return diffDays + 1;
  };

  if (loading) {
    return (
      <div style={{
        padding: 'var(--space-6)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--dark-bg)',
      }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: 'var(--space-6)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--dark-bg)',
      }}>
        <ErrorMessage message={error} />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--dark-bg)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Page Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-6) var(--space-6) var(--space-4)',
        backgroundColor: 'var(--dark-bg)',
        borderBottom: '1px solid var(--gray-600)',
        flexWrap: 'wrap',
        gap: 'var(--space-4)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <AlertCircle size={28} style={{ color: 'var(--gray-400)' }} />
          <div>
            <h1 style={{
              margin: 0,
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)',
            }}>
              Model Building Manager
            </h1>
            <p style={{
              margin: 0,
              marginTop: 'var(--space-2)',
              fontSize: 'var(--text-sm)',
              color: 'var(--gray-400)',
            }}>
              {activeTab === 'action-items'
                ? `${statistics.totalActionItems} action item${statistics.totalActionItems !== 1 ? 's' : ''}`
                : `${statistics.totalOnHold} project${statistics.totalOnHold !== 1 ? 's' : ''} on hold`}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '0 var(--space-6)',
        backgroundColor: 'var(--dark-bg)',
        borderBottom: '2px solid var(--gray-700)',
        gap: 'var(--space-6)',
      }}>
        <div style={{ display: 'flex', gap: 'var(--space-2)', paddingBottom: '2px', borderBottom: '2px solid transparent', marginBottom: '-2px' }}>
          <button
            onClick={() => {
              navigate(generateModelBuildingManagerUrl("action-items"));
              setSearchQuery("");
            }}
            className={`ops-tab ${activeTab === 'action-items' ? 'ops-tab-active' : ''}`}
          >
            <LayoutGrid size={16} />
            Action Items ({statistics.totalActionItems})
          </button>
          <button
            onClick={() => {
              navigate(generateModelBuildingManagerUrl("on-hold"));
              setSearchQuery("");
              setShowAllHoldProjects(true);
            }}
            className={`ops-tab ${activeTab === 'on-hold' ? 'ops-tab-active' : ''}`}
          >
            <Pause size={16} />
            Projects on Hold ({statistics.totalOnHold})
          </button>
          <button
            onClick={() => {
              navigate(generateModelBuildingManagerUrl("assignment-rules"));
              setSearchQuery("");
            }}
            className={`ops-tab ${activeTab === 'assignment-rules' ? 'ops-tab-active' : ''}`}
          >
            <Settings size={16} />
            Assignment Rules
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-6)' }}>
        {activeTab === "action-items" ? (
          // Action Items Tab Content
          <div>
            {/* Toggle button for Recent Reminders */}
            {recentHoldReminders.length > 0 && (
              <div style={{ marginBottom: 'var(--space-4)', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant={showRecentReminders ? "primary" : "outline"}
                  size="sm"
                  onClick={() => setShowRecentReminders(!showRecentReminders)}
                  style={{
                    fontSize: 'var(--text-sm)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                  }}
                >
                  <Clock size={16} />
                  {showRecentReminders ? 'Hide' : 'Show'} Recent Hold Reminders ({recentHoldReminders.length})
                </Button>
              </div>
            )}

            {discrepanciesForReview.length === 0 && holdProjectsPendingEmail.length === 0 && rushProjectsPendingNotification.length === 0 && softHoldPausedProjects.length === 0 && staleInStageProjects.length === 0 ? (
              <div className="empty-state">
                <FileWarning size={48} />
                <h3>No Action Items</h3>
                <p>All items have been addressed</p>
              </div>
            ) : (
              <>
                {/* Discrepancies for Review Section */}
                {discrepanciesForReview.length > 0 && (
                  <div className="section-container">
                    <div className="section-header">
                      <h2>Discrepancies for Review</h2>
                      <span className="section-count">{discrepanciesForReview.length}</span>
                    </div>
                    <div className="projects-grid">
                      {discrepanciesForReview.map(({ discrepancy, project }) => (
                        <DiscrepancyCard
                          key={discrepancy.id}
                          discrepancy={discrepancy}
                          project={project}
                          onClick={() => handleDiscrepancyClick(discrepancy)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Rush Projects Section */}
                {rushProjectsPendingNotification.length > 0 && (
                  <div className="section-container" style={{ marginTop: discrepanciesForReview.length > 0 ? '24px' : 0 }}>
                    <div className="section-header">
                      <h2>Rush Projects (&lt; 14 Days)</h2>
                      <span className="section-count">{rushProjectsPendingNotification.length}</span>
                    </div>
                    <div className="projects-grid">
                      {rushProjectsPendingNotification.map((project) => {
                        const daysUntilDue = project.due_date && project.created_at
                          ? Math.ceil((new Date(project.due_date) - new Date(project.created_at)) / (1000 * 60 * 60 * 24))
                          : 0;
                        return (
                          <div key={`rush-${project.id}`} className="project-card-wrapper">
                            <div className="hold-info-badge" style={{
                              backgroundColor: 'rgba(234, 179, 8, 0.1)',
                              borderColor: 'rgb(234, 179, 8)',
                              color: 'rgb(234, 179, 8)'
                            }}>
                              <Zap size={14} />
                              <span>Due in {daysUntilDue} days</span>
                            </div>
                            <ModelBuildingManagerCard
                              project={project}
                              emailTracking={null}
                              onClick={() => handleRushProjectReminderClick(project)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Paused 3+ Days Section */}
                {softHoldPausedProjects.length > 0 && (
                  <div className="section-container" style={{ marginTop: discrepanciesForReview.length > 0 || rushProjectsPendingNotification.length > 0 ? '24px' : 0 }}>
                    <div className="section-header">
                      <h2>Paused 3+ Days</h2>
                      <span className="section-count">{softHoldPausedProjects.length}</span>
                    </div>
                    <div className="projects-grid">
                      {softHoldPausedProjects.map((project) => (
                        <div key={`paused-${project.id}`} className="project-card-wrapper" style={{ position: 'relative' }}>
                          <div className="hold-info-badge" style={{
                            backgroundColor: 'rgba(234, 179, 8, 0.1)',
                            borderColor: 'rgb(234, 179, 8)',
                            color: 'rgb(234, 179, 8)'
                          }}>
                            <Pause size={14} />
                            <span>{calculateDaysPaused(project.softHoldDate)} days paused</span>
                          </div>
                          <ModelBuildingManagerCard
                            project={project}
                            emailTracking={null}
                            onClick={() => handlePausedProjectClick(project)}
                          />
                          {/* Make Active button */}
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            zIndex: 10,
                          }}>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMakeActive(project);
                              }}
                              style={{
                                fontSize: '11px',
                                padding: '4px 10px',
                                borderColor: 'rgb(34, 197, 94)',
                                color: 'rgb(34, 197, 94)',
                                backgroundColor: 'var(--dark-elevated)',
                              }}
                            >
                              <Play size={12} style={{ marginRight: '4px' }} />
                              Make Active
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* On Hold Reminders Section */}
                {holdProjectsPendingEmail.length > 0 && (
                  <div className="section-container" style={{ marginTop: discrepanciesForReview.length > 0 || rushProjectsPendingNotification.length > 0 || softHoldPausedProjects.length > 0 ? '24px' : 0 }}>
                    <div className="section-header">
                      <h2>On Hold Reminders</h2>
                      <span className="section-count">{holdProjectsPendingEmail.length}</span>
                    </div>
                    <div className="projects-grid">
                      {holdProjectsPendingEmail.map((project) => (
                        <div key={`hold-${project.id}`} className="project-card-wrapper">
                          <div className="hold-info-badge">
                            <Clock size={14} />
                            <span>{calculateDaysOnHold(project.holdStartDate)} days on hold</span>
                          </div>
                          <ModelBuildingManagerCard
                            project={project}
                            emailTracking={emailTrackingData.find(t => t.project_id === project.id)}
                            onClick={() => handleHoldReminderClick(project)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stale In Stage Section */}
                {staleInStageProjects.length > 0 && (
                  <div className="section-container" style={{ marginTop: discrepanciesForReview.length > 0 || rushProjectsPendingNotification.length > 0 || softHoldPausedProjects.length > 0 || holdProjectsPendingEmail.length > 0 ? '24px' : 0 }}>
                    <div className="section-header">
                      <h2>Stale In Stage (3+ Biz Days)</h2>
                      <span className="section-count">{staleInStageProjects.length}</span>
                    </div>
                    <div className="projects-grid">
                      {staleInStageProjects.map((project) => {
                        const modelingStatus = project.modelingStatus || project.modeling_status;
                        const hasSlackChannel = project.slackChannelId || project.slack_channel_id;
                        return (
                          <div key={`stale-${project.id}`} className="project-card-wrapper" style={{ position: 'relative' }}>
                            <div className="hold-info-badge" style={{
                              backgroundColor: 'rgba(139, 92, 246, 0.1)',
                              borderColor: 'rgb(139, 92, 246)',
                              color: 'rgb(139, 92, 246)'
                            }}>
                              <Timer size={14} />
                              <span>{project.daysInStage} biz days in {modelingStatus}</span>
                            </div>
                            <ModelBuildingManagerCard
                              project={project}
                              emailTracking={null}
                              onClick={() => handleStaleProjectClick(project)}
                            />
                            {/* Action buttons */}
                            <div style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              zIndex: 10,
                              display: 'flex',
                              gap: '4px',
                            }}>
                              {hasSlackChannel && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStaleProjectClick(project);
                                  }}
                                  style={{
                                    fontSize: '11px',
                                    padding: '4px 10px',
                                    borderColor: 'rgb(139, 92, 246)',
                                    color: 'rgb(139, 92, 246)',
                                    backgroundColor: 'var(--dark-elevated)',
                                  }}
                                  title="Send message to project channel"
                                >
                                  <MessageSquare size={12} style={{ marginRight: '4px' }} />
                                  Slack
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSnoozeStaleProject(project);
                                }}
                                style={{
                                  fontSize: '11px',
                                  padding: '4px 10px',
                                  borderColor: 'var(--gray-500)',
                                  color: 'var(--gray-400)',
                                  backgroundColor: 'var(--dark-elevated)',
                                }}
                                title="Snooze for 3 days"
                              >
                                <BellOff size={12} style={{ marginRight: '4px' }} />
                                Snooze
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDismissStaleProject(project);
                                }}
                                style={{
                                  fontSize: '11px',
                                  padding: '4px 8px',
                                  borderColor: 'var(--gray-600)',
                                  color: 'var(--gray-500)',
                                  backgroundColor: 'var(--dark-elevated)',
                                }}
                                title="Dismiss"
                              >
                                <X size={12} />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Recent Hold Reminders Section - Only show when toggle is on */}
                {showRecentReminders && recentHoldReminders.length > 0 && (
                  <div className="section-container" style={{ marginTop: '24px' }}>
                    <div className="section-header">
                      <h2>Recent Hold Reminders (Last 20)</h2>
                      <span className="section-count">{recentHoldReminders.length}</span>
                    </div>
                    <div className="projects-grid">
                      {recentHoldReminders.map((reminder) => (
                        <div key={`reminder-${reminder.id}`} className="project-card-wrapper" style={{ position: 'relative' }}>
                          <div className="hold-info-badge">
                            <Mail size={14} />
                            <span>{new Date(reminder.last_email_sent_date).toLocaleDateString()}</span>
                          </div>
                          <ModelBuildingManagerCard
                            project={{
                              ...reminder.projects,
                              projectName: reminder.projects?.name,
                              jobNumber: reminder.projects?.job_number,
                              rev: reminder.projects?.rev,
                            }}
                            emailTracking={reminder}
                            onClick={() => {}}
                          />
                          {/* Render buttons AFTER card so they appear on top */}
                          {reminder.email_status !== 'failed' && (
                            <div style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              zIndex: 10,
                            }}>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUndoHoldReminder(reminder);
                                }}
                                style={{
                                  fontSize: '11px',
                                  padding: '4px 10px',
                                  borderColor: 'var(--error-border)',
                                  color: 'var(--error-text)',
                                  backgroundColor: 'var(--dark-elevated)',
                                }}
                              >
                                Undo
                              </Button>
                            </div>
                          )}
                          {reminder.email_status === 'failed' && (
                            <div style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              zIndex: 10,
                              padding: '4px 10px',
                              backgroundColor: 'rgba(239, 68, 68, 0.1)',
                              border: '1px solid var(--error-border)',
                              borderRadius: 'var(--radius-md)',
                              fontSize: '11px',
                              color: 'var(--error-text)',
                              fontWeight: 600,
                            }}>
                              Cancelled
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : activeTab === "on-hold" ? (
          // Projects on Hold Tab Content
          <div>
            {/* Search and Filters Section */}
            <div style={{
              display: 'flex',
              gap: 'var(--space-5)',
              marginBottom: 'var(--space-6)',
              flexWrap: 'wrap',
            }}>
              {/* Toggle for showing all hold projects or just pending */}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant={!showAllHoldProjects ? 'primary' : 'secondary'}
                  size="md"
                  onClick={() => setShowAllHoldProjects(!showAllHoldProjects)}
                >
                  {showAllHoldProjects ? (
                    <>
                      <Mail size={16} style={{ marginRight: 'var(--space-2)' }} />
                      <span>Show All On Hold</span>
                    </>
                  ) : (
                    <>
                      <Clock size={16} style={{ marginRight: 'var(--space-2)' }} />
                      <span>Pending Emails Only</span>
                    </>
                  )}
                </Button>
              </div>

              {/* Search Bar */}
              <div style={{ flex: 1, minWidth: '300px' }}>
                <SearchBar
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by project name, job number, client, or hold notes..."
                  size="md"
                />
              </div>

              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                {/* Filter Dropdown */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: '12px 14px',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-lg)',
                  background: 'var(--dark-surface)',
                  height: '46px',
                }}>
                  <Filter size={16} style={{ color: 'var(--gray-400)' }} />
                  <select
                    value={filterHoldReason}
                    onChange={(e) => setFilterHoldReason(e.target.value)}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--gray-50)',
                      fontSize: 'var(--text-sm)',
                      cursor: 'pointer',
                      outline: 'none',
                    }}
                  >
                    <option value="all">All Hold Reasons</option>
                    <option value="auto_recurring_email">Auto-Recurring Email Enabled</option>
                    <option value="waiting_updated_pdfs">Waiting on Updated PDFs</option>
                    <option value="waiting_updated_cad">Waiting on Updated CAD Files</option>
                    <option value="waiting_discrepancy_clarification">
                      Waiting on Discrepancy Clarification
                    </option>
                    <option value="client_requested_hold">Client Requested Hold</option>
                  </select>
                </div>

                {/* Sort Dropdown */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: '12px 14px',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-lg)',
                  background: 'var(--dark-surface)',
                  height: '46px',
                }}>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--gray-50)',
                      fontSize: 'var(--text-sm)',
                      cursor: 'pointer',
                      outline: 'none',
                    }}
                  >
                    <option value="holdStartDate">Hold Start Date</option>
                    <option value="daysOnHold">Days On Hold</option>
                    <option value="projectName">Project Name</option>
                    <option value="jobNumber">Job Number</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Projects on Hold Content */}
            {filteredHoldProjects.length === 0 ? (
              <div className="empty-state">
                <AlertCircle size={48} />
                <h3>No Projects On Hold</h3>
                <p>
                  {searchQuery || filterHoldReason !== "all"
                    ? "No projects match your filters"
                    : "All hold projects have been notified"}
                </p>
              </div>
            ) : (
              <div className="section-container">
                <div className="section-header">
                  <h2>
                    {showAllHoldProjects ? 'All Projects On Hold' : 'Projects On Hold (Pending Email)'}
                  </h2>
                  <span className="section-count">{filteredHoldProjects.length}</span>
                </div>
                <div className="projects-grid">
                  {filteredHoldProjects.map((project) => (
                    <div key={project.id} className="project-card-wrapper">
                      <div className="hold-info-badge">
                        <Clock size={14} />
                        <span>{calculateDaysOnHold(project.holdStartDate)} days on hold</span>
                      </div>
                      {/* Show email sent indicator when viewing all hold projects */}
                      {showAllHoldProjects && project.holdEmailSentDate && (
                        <div className="email-sent-badge">
                          <Mail size={14} />
                          <span>Auto-Email Enrolled</span>
                        </div>
                      )}
                      <ModelBuildingManagerCard
                        project={project}
                        emailTracking={emailTrackingData.find(t => t.project_id === project.id)}
                        onClick={() => handleCardClick(project)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : activeTab === "assignment-rules" ? (
          // Assignment Rules Tab Content
          <AssignmentRulesManager />
        ) : null}
      </div>


      {modalOpen && selectedProject && (
        <>
          {console.log("ModelBuildingManager: Rendering ModelBuildingManagerModal", { modalOpen, selectedProject: selectedProject?.id })}
          <ModelBuildingManagerModal
            project={selectedProject}
            clients={clients}
            contacts={contacts}
            discrepancies={discrepancies}
            isOpen={modalOpen}
            onClose={handleModalClose}
            onSave={handleModalSave}
            onDelete={handleProjectDelete}
          />
        </>
      )}

      {holdReminderModalOpen && selectedHoldProject && (
        <OnHoldReminderModal
          project={selectedHoldProject}
          emailTracking={emailTrackingData.find(t => t.project_id === selectedHoldProject.id)}
          isOpen={holdReminderModalOpen}
          onClose={() => {
            setHoldReminderModalOpen(false);
            setSelectedHoldProject(null);
          }}
          onSuccess={async () => {
            await loadInitialData();
          }}
        />
      )}

      {rushReminderModalOpen && selectedRushProject && (
        <RushProjectReminderModal
          project={selectedRushProject}
          rushTracking={rushProjectTracking.find(t => t.project_id === selectedRushProject.id)}
          isOpen={rushReminderModalOpen}
          onClose={() => {
            setRushReminderModalOpen(false);
            setSelectedRushProject(null);
          }}
          onSuccess={async () => {
            await loadInitialData();
          }}
        />
      )}

      {showHoldModal && holdModalProject && (
        <HoldProjectModal
          project={holdModalProject}
          isOpen={showHoldModal}
          onClose={() => {
            setShowHoldModal(false);
            setHoldModalProject(null);
          }}
          onSuccess={async () => {
            await loadInitialData();
            setShowHoldModal(false);
            setHoldModalProject(null);
          }}
        />
      )}

      {discrepancyModalOpen && selectedDiscrepancy && (
        <DiscrepancyModal
          discrepancy={selectedDiscrepancy}
          isOpen={discrepancyModalOpen}
          onClose={() => {
            setDiscrepancyModalOpen(false);
            setSelectedDiscrepancy(null);
          }}
          onSave={async (discrepancyData) => {
            // The DiscrepancyModal only passes formData as first parameter
            // It handles keeping the modal open internally, so we don't close it here

            try {
              // Save the discrepancy using the service
              let savedDiscrepancy;
              if (selectedDiscrepancy?.id) {
                // Update existing discrepancy
                savedDiscrepancy = await DiscrepancyService.updateDiscrepancy(
                  selectedDiscrepancy.id,
                  discrepancyData
                );
              } else {
                // This shouldn't happen in Action Items view as we're viewing existing discrepancies
                console.error("Cannot create new discrepancy from Action Items view");
                return;
              }

              // Refresh all discrepancies
              const discrepanciesData = await ProjectService.getDiscrepancies();
              setDiscrepancies(discrepanciesData || []);

              // Don't update selectedDiscrepancy as it will reset the form in the modal
              // The modal maintains its own form state after save
              // Only update if we need to close the modal

              // Auto-disappear: If status changed to completed state, close modal
              const completedStatuses = ['Sent', 'Resolved', 'Closed', 'Cancelled'];
              if (completedStatuses.includes(discrepancyData.discrepancyStatus)) {
                // Close modal as the discrepancy will disappear from the view
                setDiscrepancyModalOpen(false);
                setSelectedDiscrepancy(null);
              }
              // Otherwise keep modal open for continued editing

              return savedDiscrepancy;
            } catch (err) {
              console.error("Error saving discrepancy:", err);
              throw err; // Let the modal handle the error
            }
          }}
          onDelete={async () => {
            // Refresh discrepancies after delete
            try {
              const discrepanciesData = await ProjectService.getDiscrepancies();
              setDiscrepancies(discrepanciesData || []);
            } catch (err) {
              console.error("Error refreshing discrepancies:", err);
            }
            setDiscrepancyModalOpen(false);
            setSelectedDiscrepancy(null);
          }}
          context="model-building-manager"
        />
      )}

      {/* Stale In Stage Slack Modal */}
      {showStaleSlackModal && selectedStaleProject && (
        <div className="slack-modal-overlay" onClick={() => setShowStaleSlackModal(false)}>
          <div className="slack-modal" onClick={(e) => e.stopPropagation()}>
            <div className="slack-modal-header">
              <div className="slack-modal-header-content">
                <div className="slack-modal-icon" style={{ background: 'rgba(139, 92, 246, 0.15)', color: 'rgb(139, 92, 246)' }}>
                  <MessageSquare size={24} />
                </div>
                <div>
                  <h3 className="slack-modal-title">Send Message</h3>
                  <p className="slack-modal-subtitle">
                    {selectedStaleProject.jobNumber || selectedStaleProject.job_number} - {selectedStaleProject.projectName || selectedStaleProject.name}
                  </p>
                </div>
              </div>
              <button className="slack-modal-close" onClick={() => setShowStaleSlackModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="slack-modal-body">
              <div className="slack-modal-info">
                <div className="slack-modal-info-row">
                  <span className="slack-modal-info-label">Stage:</span>
                  <span className="slack-modal-info-value">{selectedStaleProject.modelingStatus || selectedStaleProject.modeling_status}</span>
                </div>
                <div className="slack-modal-info-row">
                  <span className="slack-modal-info-label">Business days in stage:</span>
                  <span className="slack-modal-info-value slack-modal-info-value--warning">
                    {selectedStaleProject.daysInStage} biz days
                  </span>
                </div>
              </div>

              <div className="slack-modal-field">
                <label className="slack-modal-label">Message</label>
                <textarea
                  className="slack-modal-textarea"
                  value={staleSlackMessage}
                  onChange={(e) => setStaleSlackMessage(e.target.value)}
                  placeholder="Type your message..."
                  rows={4}
                  disabled={staleSending}
                />
                <p className="slack-modal-hint">
                  This message will be posted to the project's chat channel.
                </p>
              </div>

              {!(selectedStaleProject.slackChannelId || selectedStaleProject.slack_channel_id) && (
                <div className="slack-modal-warning">
                  <AlertCircle size={16} />
                  This project doesn't have a chat channel. Message cannot be sent.
                </div>
              )}
            </div>

            <div className="slack-modal-footer">
              <button
                className="slack-modal-btn slack-modal-btn--secondary"
                onClick={() => setShowStaleSlackModal(false)}
                disabled={staleSending}
              >
                Cancel
              </button>
              <button
                className="slack-modal-btn slack-modal-btn--primary"
                onClick={handleSendStaleSlackMessage}
                disabled={staleSending || !staleSlackMessage.trim() || !(selectedStaleProject.slackChannelId || selectedStaleProject.slack_channel_id)}
              >
                <Send size={16} />
                {staleSending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelBuildingManagerDashboard;